import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Separator } from '@/components/ui/separator'
import { SidebarTrigger } from '@/components/ui/sidebar'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { formatDate } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { fetchWorkerDetail, updateWorkerStatus, fetchWorkerProducts, createWorkerSalary } from '@/services/workers'
import type { Worker, WorkerProduct } from '@/types/workers'
import { API_BASE_URL } from '@/lib/axios'
import {
  AlertCircle,
  ArrowLeft,
  Mail,
  Calendar,
  DollarSign,
  TrendingUp,
  Wallet,
  UserCog,
  Loader2,
  Clock,
  CheckCircle,
  XCircle,
} from 'lucide-react'

function LoadingSkeleton() {
  return (
    <div className='space-y-6'>
      <Card>
        <CardHeader>
          <div className='flex items-center gap-4'>
            <Skeleton className='h-20 w-20 rounded-full' />
            <div className='space-y-2'>
              <Skeleton className='h-6 w-48' />
              <Skeleton className='h-4 w-32' />
            </div>
          </div>
        </CardHeader>
      </Card>
      <div className='grid gap-4 md:grid-cols-3'>
        <Skeleton className='h-32' />
        <Skeleton className='h-32' />
        <Skeleton className='h-32' />
      </div>
    </div>
  )
}

export default function WorkerDetail() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const [worker, setWorker] = useState<Worker | null>(null)
  const [products, setProducts] = useState<WorkerProduct[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Salary sheet state
  const [isSalarySheetOpen, setIsSalarySheetOpen] = useState(false)
  const [salaryAmount, setSalaryAmount] = useState('')
  const [salaryDescription, setSalaryDescription] = useState('')
  const [isCreatingSalary, setIsCreatingSalary] = useState(false)

  useEffect(() => {
    const loadWorker = async () => {
      if (!id) return

      try {
        setIsLoading(true)
        setError(null)
        const [workerData, productsData] = await Promise.all([
          fetchWorkerDetail(parseInt(id)),
          fetchWorkerProducts(parseInt(id)),
        ])
        setWorker(workerData)
        setProducts(productsData)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load worker details')
      } finally {
        setIsLoading(false)
      }
    }

    loadWorker()
  }, [id])

  const handleStatusChange = async (newStatus: Worker['worker_status']) => {
    if (!id || !worker || newStatus === worker.worker_status) return

    try {
      setIsUpdatingStatus(true)
      setError(null)
      const updatedWorker = await updateWorkerStatus(parseInt(id), newStatus)
      setWorker(updatedWorker)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update worker status')
    } finally {
      setIsUpdatingStatus(false)
    }
  }

  const handleCreateSalary = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!id || !worker) return

    try {
      setIsCreatingSalary(true)
      setError(null)
      await createWorkerSalary(parseInt(id), {
        amount: salaryAmount,
        description: salaryDescription || null,
      })
      // Refresh worker data to get updated totals
      const updatedWorker = await fetchWorkerDetail(parseInt(id))
      setWorker(updatedWorker)
      setIsSalarySheetOpen(false)
      setSalaryAmount('')
      setSalaryDescription('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create salary')
    } finally {
      setIsCreatingSalary(false)
    }
  }

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    }).format(num)
  }

  const getAvatarUrl = (avatar: string | null) => {
    if (!avatar) return ''
    if (avatar.startsWith('http')) return avatar
    const baseUrl = API_BASE_URL.replace(/\/api$/, '')
    return `${baseUrl}${avatar}`
  }

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
  }

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
      case 'pending':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
      case 'rejected':
        return 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
      case 'inactive':
        return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
      default:
        return 'bg-muted text-muted-foreground'
    }
  }

  return (
    <>
      <header className='flex h-16 shrink-0 items-center gap-2'>
        <div className='flex items-center gap-2 px-4'>
          <SidebarTrigger className='-ml-1' />
          <Separator
            orientation='vertical'
            className='mr-2 data-[orientation=vertical]:h-4'
          />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to='/workers'>{t('workers.title')}</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>
                  {worker
                    ? `${worker.account.first_name} ${worker.account.last_name}`
                    : t('common.loading')}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </header>

      {error && (
        <div className='mx-4 mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400'>
          <AlertCircle className='h-4 w-4' />
          <span>{error}</span>
        </div>
      )}

      <div className='flex flex-1 flex-col gap-4 p-4 pt-0'>
        <div className='flex items-center gap-2'>
          <Button variant='ghost' size='sm' asChild>
            <Link to='/workers'>
              <ArrowLeft className='mr-2 h-4 w-4' />
              {t('workers.backToWorkers')}
            </Link>
          </Button>
        </div>

        {isLoading ? (
          <LoadingSkeleton />
        ) : worker ? (
          <>
            {/* Pending Approval Banner */}
            {worker.worker_status === 'pending' && (
              <div className='rounded-lg border-2 border-yellow-400 bg-yellow-50 p-4 dark:border-yellow-600 dark:bg-yellow-950/50'>
                <div className='flex items-start gap-4'>
                  <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900'>
                    <Clock className='h-5 w-5 text-yellow-600 dark:text-yellow-400' />
                  </div>
                  <div className='flex-1'>
                    <h3 className='text-lg font-semibold text-yellow-800 dark:text-yellow-200'>
                      {t('workers.pendingApprovalRequired')}
                    </h3>
                    <p className='mt-1 text-sm text-yellow-700 dark:text-yellow-300'>
                      {t('workers.pendingApprovalDesc')}
                    </p>
                    <div className='mt-3 flex gap-2'>
                      <Button
                        size='sm'
                        className='bg-green-600 hover:bg-green-700'
                        onClick={() => handleStatusChange('accepted')}
                        disabled={isUpdatingStatus}
                      >
                        {isUpdatingStatus ? (
                          <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                        ) : (
                          <CheckCircle className='mr-2 h-4 w-4' />
                        )}
                        {t('workers.approveWorker')}
                      </Button>
                      <Button
                        size='sm'
                        variant='destructive'
                        onClick={() => handleStatusChange('rejected')}
                        disabled={isUpdatingStatus}
                      >
                        {isUpdatingStatus ? (
                          <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                        ) : (
                          <XCircle className='mr-2 h-4 w-4' />
                        )}
                        {t('workers.reject')}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Profile Header Card */}
            <Card>
              <CardHeader>
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-6'>
                    <Avatar className='h-20 w-20'>
                      <AvatarImage
                        src={getAvatarUrl(worker.account.avatar)}
                        alt={`${worker.account.first_name} ${worker.account.last_name}`}
                      />
                      <AvatarFallback className='text-2xl'>
                        {getInitials(worker.account.first_name, worker.account.last_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className='space-y-1'>
                      <CardTitle className='text-2xl'>
                        {worker.account.first_name} {worker.account.last_name}
                      </CardTitle>
                      <CardDescription className='flex items-center gap-2'>
                        <Mail className='h-4 w-4' />
                        {worker.account.email}
                      </CardDescription>
                      <div className='flex items-center gap-2 pt-1'>
                        {worker.worker_status === 'pending' ? (
                          <span className='flex items-center gap-2 rounded-full bg-yellow-100 px-3 py-1 text-sm font-semibold text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'>
                            <span className='relative flex h-2 w-2'>
                              <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-yellow-400 opacity-75'></span>
                              <span className='relative inline-flex h-2 w-2 rounded-full bg-yellow-500'></span>
                            </span>
                            {t('workers.pendingApproval')}
                          </span>
                        ) : (
                          <span
                            className={`rounded-full px-3 py-1 text-sm font-medium capitalize ${getStatusBadgeClass(worker.worker_status)}`}
                          >
                            {t(`status.${worker.worker_status}`)}
                          </span>
                        )}
                        <span className='rounded-full bg-muted px-3 py-1 text-sm font-medium capitalize'>
                          {worker.account.role}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className='flex items-center gap-2'>
                    <Sheet open={isSalarySheetOpen} onOpenChange={setIsSalarySheetOpen}>
                      <SheetTrigger asChild>
                        <Button variant='outline' size='sm'>
                          <DollarSign className='mr-2 h-4 w-4' />
                          {t('workers.issueSalary')}
                        </Button>
                      </SheetTrigger>
                      <SheetContent>
                        <SheetHeader>
                          <SheetTitle>{t('workers.issueSalary')}</SheetTitle>
                          <SheetDescription>
                            {t('workers.issueSalaryDesc')}
                          </SheetDescription>
                        </SheetHeader>
                        <form onSubmit={handleCreateSalary} className='flex flex-col gap-4 p-4'>
                          <div className='space-y-2'>
                            <Label htmlFor='salary_amount'>{t('workers.amount')}</Label>
                            <Input
                              id='salary_amount'
                              type='number'
                              step='0.01'
                              placeholder='0.00'
                              value={salaryAmount}
                              onChange={(e) => setSalaryAmount(e.target.value)}
                              required
                            />
                          </div>
                          <div className='space-y-2'>
                            <Label htmlFor='salary_description'>
                              {t('expenses.descriptionOptional')}
                            </Label>
                            <Textarea
                              id='salary_description'
                              placeholder={t('expenses.enterDescription')}
                              value={salaryDescription}
                              onChange={(e) => setSalaryDescription(e.target.value)}
                            />
                          </div>
                          <SheetFooter>
                            <Button type='submit' disabled={isCreatingSalary}>
                              {isCreatingSalary ? t('common.loading') : t('common.create')}
                            </Button>
                          </SheetFooter>
                        </form>
                      </SheetContent>
                    </Sheet>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Financial Stats */}
            <div className='grid gap-4 md:grid-cols-3'>
              <Card>
                <CardHeader className='flex flex-row items-center justify-between pb-2'>
                  <CardTitle className='text-sm font-medium'>{t('workers.totalProfit')}</CardTitle>
                  <TrendingUp className='h-4 w-4 text-green-500' />
                </CardHeader>
                <CardContent>
                  <div className='text-2xl font-bold text-green-600 dark:text-green-400'>
                    {formatCurrency(worker.total_profit)}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className='flex flex-row items-center justify-between pb-2'>
                  <CardTitle className='text-sm font-medium'>{t('workers.receivedAmount')}</CardTitle>
                  <DollarSign className='h-4 w-4 text-muted-foreground' />
                </CardHeader>
                <CardContent>
                  <div className='text-2xl font-bold'>
                    {formatCurrency(worker.received_amount)}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className='flex flex-row items-center justify-between pb-2'>
                  <CardTitle className='text-sm font-medium'>{t('workers.balance')}</CardTitle>
                  <Wallet className='h-4 w-4 text-muted-foreground' />
                </CardHeader>
                <CardContent>
                  <div
                    className={`text-2xl font-bold ${
                      parseFloat(worker.left_amount) >= 0
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                    }`}
                  >
                    {formatCurrency(worker.left_amount)}
                  </div>
                  <p className='text-xs text-muted-foreground mt-1'>
                    {parseFloat(worker.left_amount) >= 0
                      ? t('workers.amountOwed')
                      : t('workers.workerOwes')}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Status Management & Timestamps */}
            <div className='grid gap-4 md:grid-cols-2'>
              {/* Status Update */}
              <Card>
                <CardHeader className='pb-3'>
                  <CardTitle className='flex items-center gap-2 text-sm font-medium'>
                    <UserCog className='h-4 w-4' />
                    {t('workers.statusManagement')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='space-y-3'>
                    <Label htmlFor='status'>{t('workers.workerStatus')}</Label>
                    <div className='flex items-center gap-2'>
                      <Select
                        value={worker.worker_status}
                        onValueChange={(value) => handleStatusChange(value as Worker['worker_status'])}
                        disabled={isUpdatingStatus}
                      >
                        <SelectTrigger className='w-full'>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='pending'>{t('workers.pending')}</SelectItem>
                          <SelectItem value='accepted'>{t('workers.accepted')}</SelectItem>
                          <SelectItem value='rejected'>{t('workers.rejected')}</SelectItem>
                          <SelectItem value='inactive'>{t('workers.inactive')}</SelectItem>
                        </SelectContent>
                      </Select>
                      {isUpdatingStatus && (
                        <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
                      )}
                    </div>
                    <p className='text-xs text-muted-foreground'>
                      {t('workers.changeStatusHint')}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Timestamps */}
              <Card>
                <CardHeader className='pb-3'>
                  <CardTitle className='flex items-center gap-2 text-sm font-medium'>
                    <Calendar className='h-4 w-4' />
                    {t('workers.timestamps')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className='space-y-3'>
                    <div>
                      <p className='text-sm text-muted-foreground'>{t('workers.joined')}</p>
                      <p className='text-sm font-medium'>{formatDate(worker.created_at)}</p>
                    </div>
                    <div>
                      <p className='text-sm text-muted-foreground'>{t('workers.lastUpdated')}</p>
                      <p className='text-sm font-medium'>{formatDate(worker.updated_at)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Products Table */}
            <Card>
              <CardHeader>
                <CardTitle>{t('products.title')}</CardTitle>
                <CardDescription>{t('workers.productsDescription')}</CardDescription>
              </CardHeader>
              <CardContent>
                {products.length === 0 ? (
                  <p className='text-sm text-muted-foreground'>{t('workers.noProducts')}</p>
                ) : (
                  <div className='rounded-md border'>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>ID</TableHead>
                          <TableHead>{t('products.type')}</TableHead>
                          <TableHead>{t('products.material')}</TableHead>
                          <TableHead>{t('products.dimensions')}</TableHead>
                          <TableHead>{t('products.quantity')}</TableHead>
                          <TableHead>{t('products.weight')}</TableHead>
                          <TableHead>{t('products.workerPrice')}</TableHead>
                          <TableHead>{t('products.createdAt')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {products.map((product) => (
                          <TableRow key={product.id} className={product.is_deleted ? 'opacity-50' : ''}>
                            <TableCell className='font-medium'>{product.id}</TableCell>
                            <TableCell>
                              <div className='space-y-1'>
                                <span className='block capitalize'>{product.product_type}</span>
                                <span className='text-xs text-muted-foreground'>
                                  {product.type.name}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className='space-y-1'>
                                <span className='block'>{product.material.name}</span>
                                <span className='text-xs text-muted-foreground'>
                                  {product.material.thickness}mm / {product.color.name}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              {product.width} x {product.height}m
                              <span className='text-xs text-muted-foreground block'>
                                {t('products.cell')}: {product.square}
                              </span>
                            </TableCell>
                            <TableCell>{product.quantity}</TableCell>
                            <TableCell>
                              {product.weight ?? '0'} kg
                              <span className='text-xs text-muted-foreground block'>
                                / {product.full_weight ?? '0'} kg
                              </span>
                            </TableCell>
                            <TableCell>${product.worker_price ?? '0'}</TableCell>
                            <TableCell>{formatDate(product.created_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        ) : (
          <Card>
            <CardContent className='py-8 text-center text-muted-foreground'>
              {t('workers.workerNotFound')}
            </CardContent>
          </Card>
        )}
      </div>
    </>
  )
}
